import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { lazy, Suspense, useState, useEffect } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AdminProvider } from "@/contexts/AdminContext";
import { DeleteConfirmProvider } from "@/contexts/DeleteConfirmContext";
import { AgeGateProvider } from "@/contexts/AgeGateContext";
import { LayoutProvider } from "@/contexts/LayoutContext";
import { BrandingProvider, useBranding } from "@/contexts/BrandingContext";
import AgeGateWrapper from "@/components/AgeGateWrapper";
import AITapFinder from "@/components/AITapFinder";
import { Beer, Tv, Settings, LogOut } from "lucide-react";
import "@/App.css";

// Lazy load pages for code splitting - improves initial load time
// Customer-facing pages (TVDisplay, Homepage) load immediately, admin pages load on demand
const TVDisplay = lazy(() => import("@/pages/TVDisplay"));
const PublicHomepage = lazy(() => import("@/pages/PublicHomepage"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const TasteQuiz = lazy(() => import("@/pages/TasteQuiz"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ExplorerLogin = lazy(() => import("@/pages/ExplorerLogin"));
const ExplorerJournal = lazy(() => import("@/pages/ExplorerJournal"));
const SubmitBlend = lazy(() => import("@/pages/SubmitBlend"));
const AIFlightCreator = lazy(() => import("@/pages/AIFlightCreator"));
const SharePage = lazy(() => import("@/pages/SharePage"));
const BeerPassport = lazy(() => import("@/pages/BeerPassport"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
      <span className="text-zinc-400 text-sm">Loading...</span>
    </div>
  </div>
);

// Protected route wrapper for admin routes
const ProtectedAdminRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Age-gated route wrapper for public routes
const AgeGatedRoute = ({ children }) => (
  <AgeGateWrapper>
    {children}
  </AgeGateWrapper>
);

// Admin Navigation - only shown on admin routes
const AdminNavigation = () => {
  const location = useLocation();
  const { isAuthenticated, admin, logout } = useAuth();
  const { siteTitle } = useBranding();
  
  // Only show navigation on admin panel route when authenticated
  if (!location.pathname.startsWith('/admin') || location.pathname === '/admin/login') return null;
  if (!isAuthenticated) return null;
  
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center glow-amber">
              <Beer className="w-5 h-5 text-black" />
            </div>
            <div>
              <span className="font-heading text-2xl font-bold text-white group-hover:text-amber-500 transition-colors duration-300">
                {siteTitle}
              </span>
              <p className="text-xs text-zinc-500">Admin Dashboard</p>
            </div>
          </Link>
          
          <div className="flex items-center gap-2">
            <Link
              to="/admin"
              data-testid="nav-admin"
              className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 ${
                location.pathname === '/admin' 
                  ? 'bg-amber-500 text-black font-semibold' 
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <Settings className="w-4 h-4" />
              <span>Manage</span>
            </Link>
            <Link
              to="/tv"
              data-testid="nav-tv-display"
              className="flex items-center gap-2 px-4 py-2 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all duration-300"
            >
              <Tv className="w-4 h-4" />
              <span>Customer View</span>
            </Link>
            
            {/* User info and logout */}
            <div className="flex items-center gap-3 ml-4 pl-4 border-l border-zinc-700">
              <span className="text-zinc-400 text-sm">
                {admin?.username}
                {admin?.is_super && <span className="ml-1 text-amber-500">(Super)</span>}
              </span>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-3 py-2 rounded-full text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all duration-300"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

// TV Device Detection - same logic as TVDisplay.jsx
const checkIsTVDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return ua.includes("AFT") ||           // Fire TV
         ua.includes("Roku") ||          // Roku
         ua.includes("AppleTV") || ua.includes("Apple TV") ||  // Apple TV
         ua.includes("GoogleTV") || ua.includes("Google TV") || ua.includes("Chromecast") ||  // Google TV
         ua.includes("SmartTV") || ua.includes("SMART-TV") ||  // Generic Smart TV
         ua.includes("Tizen") ||         // Samsung TV
         ua.includes("webOS") ||         // LG TV
         ua.includes("NetCast") ||       // LG older
         ua.includes("VIDAA") ||         // Hisense
         ua.includes("Viera") ||         // Panasonic
         ua.includes("Philips") ||
         ua.includes("Opera TV") ||
         ua.includes("HbbTV");
};

// Global AI Tap Finder - shows on all customer pages except actual TV devices and admin
const GlobalAITapFinder = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isTVDevice, setIsTVDevice] = useState(false);
  
  // Check if running on a TV device on mount
  useEffect(() => {
    setIsTVDevice(checkIsTVDevice());
  }, []);
  
  // Don't show on admin pages or actual TV devices
  const isAdminPage = location.pathname.startsWith('/admin');
  
  if (isAdminPage || isTVDevice) return null;
  
  return (
    <AITapFinder 
      isOpen={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
    />
  );
};

function AppRoutes() {
  return (
    <>
      <AdminNavigation />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ============================================ */}
          {/* PUBLIC CUSTOMER-FACING ROUTES (Age-Gated) */}
          {/* ============================================ */}
          
          {/* Root route - Marketing Homepage (customer landing page) */}
          <Route path="/" element={<AgeGatedRoute><PublicHomepage /></AgeGatedRoute>} />
          
          {/* Homepage - Also accessible via /home */}
          <Route path="/home" element={<AgeGatedRoute><PublicHomepage /></AgeGatedRoute>} />
          
          {/* TV Display - Customer tap wall view */}
          <Route path="/tv" element={<AgeGatedRoute><TVDisplay /></AgeGatedRoute>} />
          
          {/* Customer features - Age-Gated */}
          <Route path="/quiz" element={<AgeGatedRoute><TasteQuiz /></AgeGatedRoute>} />
          <Route path="/submit" element={<AgeGatedRoute><SubmitBlend /></AgeGatedRoute>} />
          <Route path="/ai-flight-creator" element={<AgeGatedRoute><AIFlightCreator /></AgeGatedRoute>} />
          
          {/* Explorer (customer) routes - Age-Gated */}
          <Route path="/explorer-login" element={<AgeGatedRoute><ExplorerLogin /></AgeGatedRoute>} />
          <Route path="/journal" element={<AgeGatedRoute><ExplorerJournal /></AgeGatedRoute>} />
          <Route path="/passport" element={<AgeGatedRoute><BeerPassport /></AgeGatedRoute>} />
          
          {/* Share pages - No age gate for social sharing */}
          <Route path="/share/:type/:id" element={<SharePage />} />
          
          {/* ============================================ */}
          {/* ADMIN ROUTES (No Age Gate - Auth Required) */}
          {/* ============================================ */}
          
          {/* Admin login - dedicated route */}
          <Route path="/admin/login" element={<LoginPage />} />
          
          {/* Admin registration */}
          <Route path="/admin/register" element={<RegisterPage />} />
          
          {/* Protected admin panel */}
          <Route 
            path="/admin" 
            element={
              <ProtectedAdminRoute>
                <AdminPanel />
              </ProtectedAdminRoute>
            } 
          />
          
          {/* Legacy login route - redirect to admin login */}
          <Route path="/login" element={<Navigate to="/admin/login" replace />} />
          <Route path="/register" element={<Navigate to="/admin/register" replace />} />
        </Routes>
      </Suspense>
      {/* Global AI Tap Finder Widget - shows on customer pages except TV */}
      <GlobalAITapFinder />
      <Toaster position="bottom-right" theme="dark" />
    </>
  );
}

// Dynamic document title from branding
const DynamicTitle = () => {
  const { brandName, brandDescription } = useBranding();
  useEffect(() => {
    if (brandName) document.title = brandName;
    const meta = document.querySelector('meta[name="description"]');
    if (meta && brandDescription) meta.setAttribute("content", brandDescription);
  }, [brandName, brandDescription]);
  return null;
};

function App() {
  return (
    <div className="app-container">
      <BrowserRouter>
        <BrandingProvider>
          <LayoutProvider>
            <AgeGateProvider>
              <AuthProvider>
                <AdminProvider>
                  <DeleteConfirmProvider>
                    <DynamicTitle />
                    <AppRoutes />
                  </DeleteConfirmProvider>
                </AdminProvider>
              </AuthProvider>
            </AgeGateProvider>
          </LayoutProvider>
        </BrandingProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
